<template>
    <div>
        <div class="mt-3 flex justify-between">
            <div class="flex flex-wrap gap-2">
                <button @click="setSection('nexa_quick_guide')">
                    <BadgePill :color="sections['nexa_quick_guide'] ? 'indigo' : 'gray'">
                        {{ $t('messages.nexa_quick_guide') }}
                    </BadgePill>
                </button>
                <button @click="setSection('survey_answers')">
                    <BadgePill :color="sections['survey_answers'] ? 'indigo' : 'gray'">
                        {{ $t('messages.survey_answers') }}
                    </BadgePill>
                </button>
                <button @click="setSection('sociograms')">
                    <BadgePill :color="sections['sociograms'] ? 'indigo' : 'gray'">
                        {{ $tc('messages.sociograms', 2) }}
                    </BadgePill>
                </button>
                <button @click="setSection('generate_teams')">
                    <BadgePill :color="sections['generate_teams'] ? 'indigo' : 'gray'">
                        {{ $t('messages.configuration') }}
                    </BadgePill>
                </button>
            </div>

            <div v-if="sociogramSurvey.sociogramRecords.length">
                <div class="flex justify-end">
                    <AppButton type="submit" element="button" form="generate-teams-form">
                        {{ $t('messages.generate_teams') }}
                    </AppButton>
                </div>
            </div>
        </div>

        <PageSectionCollapsible v-model:is-open="sections.nexa_quick_guide" id="nexa_quick_guide" class="mt-5">
            <template v-slot:title>
                {{ $t('messages.nexa_quick_guide') }}
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <p class="mt-1 whitespace-pre-line col-span-8">
                    {{ $t('messages.nexa_quick_guide_explanation') }}
                </p>
                <div class="col-span-4 hidden md:block">
                    <img src="/img/team_generator/nexa_guide_image_sociogram_show.webp" alt="" />
                </div>
            </div>
        </PageSectionCollapsible>

        <PageSectionCollapsible id="survey_answers" class="mt-5" v-model:is-open="sections.survey_answers">
            <template v-slot:title>
                <div class="flex gap-4">
                    <div>
                        {{ $t('messages.survey_answers') }}
                    </div>
                    <div class="flex gap-1">
                        <span>(</span>
                        <span>{{ sociogramSurvey.sociogramRecords.length }}</span>
                        <span>/</span>
                        <span>{{
                            sociogramSurvey.sociogramRecords.length + sociogramSurvey.studentsWithoutRecord.length
                        }}</span>
                        <span>)</span>
                    </div>
                </div>
            </template>

            <template v-slot:subtitle>
                {{ $t('messages.sociogram_answers_section_subtitle') }}
            </template>

            <AppCard>
                <div class="p-2">
                    <PageSectionCollapsible id="without_responses" class="mt-5" is-open>
                        <template v-slot:title>
                            <div>
                                {{ $t('messages.without_responses') }} ({{
                                    sociogramSurvey.studentsWithoutRecord.length
                                }})
                            </div>
                        </template>

                        <div v-if="sociogramSurvey.studentsWithoutRecord.length" class="divide-gray-100 divide-y">
                            <div class="md:grid md:grid-cols-2 md:gap-6">
                                <AppCard
                                    v-for="student in sociogramSurvey.studentsWithoutRecord"
                                    :key="student.id"
                                    class="mt-1"
                                >
                                    <div class="flex items-center justify-between gap-2 py-2 px-2">
                                        <div class="flex gap-1">
                                            <StudentInfoItem :student="student" />
                                        </div>
                                        <div class="flex items-center justify-end gap-2">
                                            <div></div>
                                        </div>
                                    </div>
                                </AppCard>
                            </div>
                            <div class="mt-3"><br /></div>
                        </div>
                    </PageSectionCollapsible>

                    <PageSectionCollapsible id="survey_answers_responses" class="mt-5">
                        <template v-slot:title>
                            <div>
                                {{ $t('messages.survey_answers') }}
                            </div>
                        </template>

                        <div
                            class="md:grid md:grid-cols-2 md:gap-6 mt-3"
                            v-if="sociogramSurvey.sociogramRecords.length"
                        >
                            <AppCard v-for="record in sociogramSurvey.sociogramRecords" :key="record.id" class="mt-1">
                                <div class="flex items-center justify-between gap-2 py-2 px-2">
                                    <div class="flex gap-1">
                                        <StudentInfoItem :student="record.student" />
                                    </div>
                                    <div class="flex items-center justify-end gap-2">
                                        <div class="text-gray-500 text-sm">
                                            {{ record.date_display }}
                                        </div>
                                        <div v-if="meta.can.delete_records">
                                            <InertiaSquareDeleteButton
                                                :href="record.links.delete"
                                                :confirm-text="$t('messages.are_you_sure?')"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </AppCard>
                        </div>
                    </PageSectionCollapsible>
                </div>
            </AppCard>
        </PageSectionCollapsible>

        <PageSectionCollapsible id="sociograms" class="mt-5" v-model:is-open="sections.sociograms">
            <template v-slot:title>
                {{ $tc('messages.sociograms', 2) }}
            </template>
            <template v-slot:subtitle>
                {{ $t('messages.sociogram_section_subtitle') }}
            </template>

            <div v-for="(item, itemIndex) in sociogramSurvey.items" :key="item.id">
                <AppWell>
                    <AppCard>
                        <details :open="itemIndex === 0" class="p-1">
                            <summary>
                                <span class="font-semibold">{{ item.title }}</span>
                            </summary>
                            <div class="mt-1">
                                <template v-if="item.type === 'relationship'">
                                    <NetworkGraphSociogramStudents
                                        :students="item.students"
                                        :edges="item.edges"
                                        :is-negative="item.subtype === 'negative'"
                                    />
                                </template>
                            </div>
                        </details>
                    </AppCard>
                </AppWell>
            </div>
        </PageSectionCollapsible>

        <PageSectionCollapsible
            id="generate_teams_based_on_sociogram_survey"
            class="mt-5"
            v-model:is-open="sections.generate_teams"
        >
            <template v-slot:title>
                {{ $t('messages.configuration') }}
            </template>
            <template v-slot:subtitle>
                {{ $t('messages.generate_teams_section_subtitle') }}
            </template>

            <div id="generate-teams">
                <AppWell>
                    <form :action="meta.links.group_teams_edit" id="generate-teams-form">
                        <div class="text-gray-700">
                            {{ $t('messages.select_survey_items_to_generate_teams_based_on') }}
                        </div>
                        <div class="md:grid md:grid-cols-2 md:gap-6">
                            <div>
                                <IconThumbUpFilled
                                    class="w-8 h-8 text-green-400 mr-3 inline-block"
                                    style="color: #68d391"
                                />

                                <ul>
                                    <li
                                        v-for="item in sociogramSurvey.items.filter((i) => i.subtype === 'positive')"
                                        :key="item.id"
                                    >
                                        <label>
                                            <input
                                                type="radio"
                                                name="positive_sociogram_item_id"
                                                :id="item.id"
                                                :value="item.id"
                                                :checked="item.is_default"
                                                required
                                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                                            />
                                            {{ item.title }}</label
                                        >
                                    </li>
                                </ul>
                            </div>
                            <div>
                                <IconThumbDownFilled
                                    class="w-8 h-8 text-red-400 mr-3 inline-block"
                                    style="color: #fc8181"
                                />

                                <ul>
                                    <li
                                        v-for="item in sociogramSurvey.items.filter((i) => i.subtype === 'negative')"
                                        :key="item.id"
                                    >
                                        <label>
                                            <input
                                                type="radio"
                                                name="negative_sociogram_item_id"
                                                :id="item.id"
                                                :value="item.id"
                                                :checked="item.is_default"
                                                required
                                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300"
                                            />
                                            {{ item.title }}
                                        </label>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <input type="hidden" name="sociogram_survey_id" :value="sociogramSurvey.id" />

                        <div class="mt-3">
                            <div>
                                {{ $t('messages.go_to_assisted_team_generator') }}
                            </div>
                            <AppButton type="submit" element="button">
                                {{ $t('messages.generate') }}
                            </AppButton>
                        </div>
                    </form>
                </AppWell>
            </div>
        </PageSectionCollapsible>
    </div>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import AppCard from '@/Shared/AppCard.vue';
import AppWell from '@/Shared/AppWell.vue';
import NetworkGraphSociogramStudents from '@/components/pageSpecific/NetworkGraphSociogramStudents.vue';
import InertiaSquareDeleteButton from '@/components/InertiaSquareDeleteButton.vue';
import StudentInfoItem from '@/components/StudentInfoItem.vue';
import AppButton from '@/components/AppButton.vue';
import { IconThumbDownFilled, IconThumbUpFilled } from '@tabler/icons-vue';
import PageSectionCollapsible from '../../../../Shared/PageSectionCollapsible.vue';
import BadgePill from '../../../../components/BadgePill.vue';

export default {
    components: {
        BadgePill,
        PageSectionCollapsible,
        AppButton,
        StudentInfoItem,
        InertiaSquareDeleteButton,
        NetworkGraphSociogramStudents,
        AppWell,
        AppCard,
        IconThumbDownFilled,
        IconThumbUpFilled,
    },
    layout: Layout,
    name: 'SociogramSurveyShowPage',
    props: ['meta', 'sociogramSurvey'],
    data() {
        return {
            sections: {
                nexa_quick_guide: true,
                sociograms: false,
                survey_answers: false,
                generate_teams: false,
            },
        };
    },
    methods: {
        setSection(section) {
            ['nexa_quick_guide', 'survey_answers', 'sociograms', 'generate_teams'].forEach((s) => {
                if (s === section) {
                    this.sections[s] = !this.sections[s];
                } else {
                    this.sections[s] = false;
                }
            });
        },
    },
};
</script>
